import type { DeviceGenre } from '@/constants/device';
import type { DeviceTaskStatus, ListSelfReportTargetsResponse, ListSelfReportsResponse } from '../../_root/packages/-components/DeviceInventoryOperation/query/type';
import type { DeviceInventoryDevice, DeviceStatus, ReportStatus } from '../../_root/packages/-components/DeviceInventoryOperation/type';
import { isSelfReportResult, isSelfReportTarget } from './typeGuards';
import type { ApiDeviceResult } from './types';
import { validateDeviceCondition } from './validators';

/**
 * レポートステータスのマッピング
 */
export function mapApiStatusToReportStatus(selfReport: ApiDeviceResult['selfReport'] | undefined): ReportStatus {
  return selfReport ? 'REPORTED' : 'NOT_REPORTED';
}

/**
 * デバイスステータスの決定
 */
export function determineDeviceStatus(selfReport: ApiDeviceResult['selfReport'] | undefined): DeviceStatus {
  if (!selfReport) {
    return 'UNREPORTED';
  }
  const condition = validateDeviceCondition(selfReport.deviceCondition);
  return condition || 'UNREPORTED';
}

/**
 * APIレスポンスから安全にデバイス情報を変換
 */
export function mapApiDeviceToDevice(apiDevice: ApiDeviceResult): DeviceInventoryDevice {
  try {
    if (!apiDevice.device?.id) {
      throw new Error('Device ID is required');
    }
    if (!apiDevice.device.name) {
      throw new Error('Device name is required');
    }
    const device: DeviceInventoryDevice = {
      packageInstanceId: apiDevice.instanceId,
      taskInstanceId: apiDevice.taskInstanceId,
      deviceTaskId: apiDevice.deviceTaskId,
      displayStatus: apiDevice.displayStatus || 'REQUEST_BEFORE',
      taskStatus: apiDevice.status,
      deviceId: apiDevice.device.id,
      deviceName: apiDevice.device.name,
      deviceTag: apiDevice.device.deviceTag || undefined,
      deviceImageUrl: apiDevice.device.avatarUrl || undefined,
      deviceGenre: apiDevice.device.genre as DeviceGenre,
      deviceModel: apiDevice.device.model,
      assigneeType: 'assigned' as const,
      status: determineDeviceStatus(apiDevice.selfReport),
      reportStatus: mapApiStatusToReportStatus(apiDevice.selfReport),
      selfReportResult: apiDevice.selfReport ? {
        reportedAt: apiDevice.selfReport.reportedAt || apiDevice.anchorDate || new Date().toISOString(),
        reportedBy: apiDevice.selfReport.reportedByPersonId || 'unknown',
        reportedByPerson: apiDevice.selfReport.reportedByPerson ? {
          id: apiDevice.selfReport.reportedByPerson.id,
          name: apiDevice.selfReport.reportedByPerson.name,
          avatarUrl: apiDevice.selfReport.reportedByPerson.avatarUrl ?? undefined
        } : undefined,
        deviceCondition: validateDeviceCondition(apiDevice.selfReport.deviceCondition) || 'UNREPORTED',
        reportResult: apiDevice.selfReport.reportResult,
        verificationMethod: apiDevice.selfReport.verificationMethod,
        memo: apiDevice.selfReport.memo ?? undefined
      } : undefined,
      assignee: apiDevice.assignedPerson ? {
        id: apiDevice.assignedPerson.id,
        name: apiDevice.assignedPerson.name,
        avatarUrl: apiDevice.assignedPerson.avatarUrl ?? undefined
      } : undefined,
      responsiblePerson: apiDevice.responsiblePerson ? {
        id: apiDevice.responsiblePerson.id,
        name: apiDevice.responsiblePerson.name,
        avatarUrl: apiDevice.responsiblePerson.avatarUrl ?? undefined
      } : undefined,
      owner: apiDevice.owner ? {
        id: apiDevice.owner.id,
        name: apiDevice.owner.name,
        avatarUrl: apiDevice.owner.avatarUrl ?? undefined
      } : undefined,
      deviceUser: apiDevice.deviceUser ? {
        id: apiDevice.deviceUser.id,
        name: apiDevice.deviceUser.name,
        avatarUrl: apiDevice.deviceUser.avatarUrl ?? undefined
      } : undefined,
      lastUpdatedAt: apiDevice.anchorDate || new Date().toISOString(),
      actionMemo: apiDevice.actionMemo,
      anchorDate: apiDevice.anchorDate,
      lastInventoryDate: apiDevice.device.lastInventoryDate ? new Date(apiDevice.device.lastInventoryDate) : null
    };
    return device;
  } catch (error) {
    console.error('Failed to map API device to device:', error, apiDevice);
    throw new Error(`Invalid device data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * セルフレポート対象デバイスをApiDeviceResult形式に変換
 */
export function convertSelfReportTargetToApiDevice(target: ListSelfReportTargetsResponse['items'][number]): ApiDeviceResult | null {
  if (!isSelfReportTarget(target)) {
    return null;
  }
  return {
    instanceId: target.instanceId,
    taskInstanceId: target.taskInstanceId,
    deviceTaskId: target.deviceTaskId,
    device: target.device,
    status: 'NotStarted' as DeviceTaskStatus,
    assignedPerson: target.assignedPerson,
    requestId: target.requestId,
    displayStatus: target.displayStatus,
    anchorDate: target.anchorDate,
    responsiblePerson: target.responsiblePerson,
    owner: target.owner,
    deviceUser: target.deviceUser
  };
}

/**
 * セルフレポート結果をApiDeviceResult形式に変換
 */
export function convertSelfReportResultToApiDevice(result: ListSelfReportsResponse['items'][number]): ApiDeviceResult | null {
  if (!isSelfReportResult(result)) {
    return null;
  }
  return {
    instanceId: result.instanceId,
    taskInstanceId: result.taskInstanceId,
    deviceTaskId: result.deviceTaskId,
    device: {
      ...result.device,
      lastInventoryDate: null
    },
    status: result.status,
    selfReport: result.selfReport || undefined,
    assignedPerson: result.assignedPerson,
    anchorDate: result.anchorDate,
    requestId: result.requestId,
    displayStatus: result.displayStatus,
    actionMemo: result.actionMemo
  };
}

/**
 * 複数のAPIデバイスを安全に変換
 */
export function mapApiDevicesToDevices(apiDevices: ApiDeviceResult[]): DeviceInventoryDevice[] {
  const devices: DeviceInventoryDevice[] = [];
  const errors: Error[] = [];
  for (const [index, apiDevice] of apiDevices.entries()) {
    try {
      const device = mapApiDeviceToDevice(apiDevice);
      devices.push(device);
    } catch (error) {
      errors.push(new Error(`Device at index ${index}: ${error instanceof Error ? error.message : 'Unknown error'}`));
    }
  }
  if (errors.length > 0) {
    console.warn(`Failed to convert ${errors.length} devices:`, errors);
  }
  return devices;
}